import { useState } from "react";
import { toast } from "sonner";
import { CalendarClock, XCircle, Loader2, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { SlotPicker } from "@/components/booking/SlotPicker";

export function ManageActions({ token, booking, onChange }) {
  const [mode, setMode] = useState(null);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);

  const call = async (path, body, okMsg) => {
    setBusy(true);
    try { const r = await api.post(`/bookings/public/${token}/${path}`, body, { timeout: 60000 }); onChange(r.data); setMode(null); setSlot(null); toast.success(okMsg); }
    catch (e) { toast.error(e?.response?.data?.detail || "That didn't work — please call 310-422-0669"); if (e?.response?.status === 409) setSlot(null); }
    finally { setBusy(false); }
  };

  if (mode === "reschedule") {
    return (
      <div className="mt-6" data-testid="manage-reschedule">
        <SlotPicker serviceId={booking.service_id} value={slot} onChange={setSlot} days={30} />
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button type="button" onClick={() => call("reschedule", { start_at: slot.start_at }, "Your session has been moved")} disabled={!slot || busy} className="dg-btn-primary rounded-full px-6 py-2 text-sm flex items-center gap-2 disabled:opacity-40" data-testid="reschedule-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />} {slot ? `Move to ${slot.label} Pacific` : "Pick a new time"}
          </button>
          <button type="button" onClick={() => { setMode(null); setSlot(null); }} disabled={busy} className="dg-btn-ghost rounded-full px-5 py-2 text-sm" data-testid="reschedule-back-btn">Keep current time</button>
        </div>
      </div>
    );
  }

  if (mode === "cancel") {
    return (
      <div className="mt-6 rounded-xl border border-rose-500/30 bg-rose-500/10 p-5" data-testid="manage-cancel-confirm">
        <div className="font-semibold text-rose-100">Cancel this session?</div>
        <p className="text-sm text-rose-100/80 mt-1">{booking.payment_status === "paid" ? "Your payment stays on file as credit toward a new session — David will reach out to rebook you." : "No charge — nothing is owed for a cancelled session."}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button type="button" onClick={() => call("cancel", {}, "Session cancelled")} disabled={busy} className="rounded-full px-6 py-2 text-sm bg-rose-500 text-white font-semibold hover:bg-rose-400 flex items-center gap-2 disabled:opacity-40" data-testid="cancel-confirm-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Yes, cancel it
          </button>
          <button type="button" onClick={() => setMode(null)} disabled={busy} className="dg-btn-ghost rounded-full px-5 py-2 text-sm" data-testid="cancel-back-btn">Keep my session</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 flex flex-wrap gap-3" data-testid="manage-actions">
      {booking.can_reschedule && (
        <button type="button" onClick={() => setMode("reschedule")} className="dg-btn-primary rounded-full px-6 py-3 text-sm flex items-center gap-2" data-testid="reschedule-btn">
          <CalendarClock className="w-4 h-4" /> Pick a new time
        </button>
      )}
      <button type="button" onClick={() => setMode("cancel")} className="dg-btn-ghost rounded-full px-6 py-3 text-sm flex items-center gap-2" data-testid="cancel-btn">
        <XCircle className="w-4 h-4" /> Cancel session
      </button>
      <p className="w-full text-xs text-slate-500">Self-service up to {booking.modify_cutoff_hours} hours before your session. Closer than that? Call 310-422-0669.</p>
    </div>
  );
}
